import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Select, Store } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ButtonModule, NumberfieldModule, TextfieldModule } from '@ed---interne/ng-uui-components';
import { BillingMilestoneModel, SubcontractorInvoiceModel } from '../../../project/project.models';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { HeaderService } from 'src/app/shared/services/header.service';
import { combineLatest, Observable } from 'rxjs';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { DashboardState, GetBillingMilestones } from '../../dashboard.state';
import { ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis } from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';

@UntilDestroy()
@Component({
    selector: 'dashboard-billing',
    templateUrl: './dashboard-billing.component.html',
    styleUrls: ['./dashboard-billing.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
    ],
})
export class DashboardBillingComponent {
    public now: Date = new Date();

    @Select(DashboardState.year)
    public year$!: Observable<number>;
    public year: string = String(this.now.getFullYear());
    public yearsOptions: { value: string; label: string }[] = Array.from(Array(5).keys()).map(
        (n) => ({ value: String(Number(this.year) - n), label: String(Number(this.year) - n) }),
    );

    @Select(DashboardState.billingMilestones)
    public billingMilestones$!: Observable<BillingMilestoneModel[]>;
    public billingMilestones: BillingMilestoneModel[] = [];

    @Select(DashboardState.subcontractorInvoices)
    public subcontractorInvoices$!: Observable<SubcontractorInvoiceModel[]>;
    public subcontractorInvoices: SubcontractorInvoiceModel[] = [];

    @Select(DashboardState.isLoading)
    public isLoading$!: Observable<boolean>;
    public isLoading: boolean = true;

    public billingSentData = new MatTableDataSource<{}>([]);
    public billingPendingData = new MatTableDataSource<{}>([]);

    public allBillingTotal: number = 0;
    public allBillingPending: number = 0;

    public thisYearBillingTotal: number = 0;
    public thisYearBillingPending: number = 0;

    public thisMonthBillingTotal: number = 0;
    public thisMonthBillingPending: number = 0;

    public lateBilling: number = 0;

    public billingPendingColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date prévisionnelle',
            objectKey: 'plannedDate',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code Projet',
            objectKey: 'code',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Label',
            objectKey: 'label',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant',
            objectKey: 'amountExclTax',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'CP',
            objectKey: 'leadLabel',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Activité',
            objectKey: 'activityLabel',
            bold: false,
        },
    ];

    public billingSentColumns: DisplayedColumns[] = [
        ...this.billingPendingColumns,
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code facture',
            objectKey: 'invoiceNumber',
            bold: true,
        },
    ];

    billingXaxisCategories: ApexXAxis['categories'] = [];
    billingSeries: ApexAxisChartSeries = [];
    billingDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [0, 1] };
    billingYaxis: ApexYAxis[] = [
        {
            seriesName: ['Montant', 'En attente'],
            title: {
                text: 'Montant',
            },
            labels: {
                formatter: (value: number) => convertNumber(value),
            },
        },
    ];

    private firstDayOfMonth(date: Date) {
        let newDate = new Date(date);
        newDate = new Date(`${newDate.getFullYear()}-${newDate.getMonth() + 1}-02`);
        return newDate;
    }

    constructor(
        private readonly router: Router,
        private readonly headerService: HeaderService,
        private readonly store: Store,
    ) {
        this.isLoading$.pipe(untilDestroyed(this)).subscribe((isLoading: boolean) => {
            this.isLoading = isLoading;
        });
        this.year$.pipe(untilDestroyed(this)).subscribe((year: number) => {
            this.year = String(year);
        });
        combineLatest([this.billingMilestones$, this.subcontractorInvoices$])
            .pipe(untilDestroyed(this))
            .subscribe(([billingMilestones, subcontractorInvoices]) => {
                if (!billingMilestones) return;
                this.billingMilestones = billingMilestones;

                this.subcontractorInvoices = subcontractorInvoices || [];

                //Chart :

                const monthly: any[] = this.billingMilestones
                    .filter((d) => new Date(d.plannedDate).getFullYear() >= Number(this.year))
                    .reduce((acc, b) => {
                        const exist: any = acc.find(
                            (a: any) =>
                                a.isSent === b.isSent &&
                                a.plannedDate.getTime() ===
                                    this.firstDayOfMonth(b.plannedDate).getTime(),
                        );
                        if (exist) {
                            exist.amountExclTax = exist.amountExclTax + b.amountExclTax;
                        } else {
                            const toAdd = {
                                plannedDate: this.firstDayOfMonth(b.plannedDate),
                                isSent: b.isSent,
                                amountExclTax: b.amountExclTax,
                                activityLabel: b.project?.activity?.label,
                            };
                            acc.push(toAdd);
                        }
                        return acc;
                    }, [] as any[]);

                const monthlySub: any[] = this.subcontractorInvoices
                    .filter((d) => new Date(d.plannedDate).getFullYear() >= Number(this.year))
                    .reduce((acc, b) => {
                        const exist: any = acc.find(
                            (a: any) =>
                                a.isSent === b.isSent &&
                                a.plannedDate.getTime() ===
                                    this.firstDayOfMonth(b.plannedDate).getTime(),
                        );
                        if (exist) {
                            exist.amountExclTax = exist.amountExclTax + b.amountExclTax;
                        } else {
                            const toAdd = {
                                plannedDate: this.firstDayOfMonth(b.plannedDate),
                                isSent: b.isSent,
                                amountExclTax: b.amountExclTax,
                                activityLabel: b.project?.activity?.label,
                            };
                            acc.push(toAdd);
                        }
                        return acc;
                    }, [] as any[]);

                this.billingXaxisCategories = monthly
                    .map((x) => x.plannedDate)
                    .sort((a, b) => (a > b ? -1 : 1));

                this.billingSeries = [
                    {
                        name: 'Facturé',
                        type: 'column',
                        data: this.billingXaxisCategories.map(
                            (d: Date) =>
                                monthly.find(
                                    (b) => b.isSent && b.plannedDate.getTime() === d.getTime(),
                                )?.amountExclTax || 0,
                        ),
                        color: '#03b803',
                    },
                    {
                        name: 'En retard',
                        type: 'column',
                        data: this.billingXaxisCategories.map(
                            (d: Date) =>
                                monthly.find(
                                    (b) =>
                                        !b.isSent &&
                                        b.plannedDate.getTime() === d.getTime() &&
                                        d.getTime() <= this.now.getTime(),
                                )?.amountExclTax || 0,
                        ),
                        color: '#ff0000',
                    },
                    {
                        name: 'Prévues',
                        type: 'column',
                        data: this.billingXaxisCategories.map(
                            (d: Date) =>
                                monthly.find(
                                    (b) =>
                                        !b.isSent &&
                                        b.plannedDate.getTime() === d.getTime() &&
                                        d.getTime() > this.now.getTime(),
                                )?.amountExclTax || 0,
                        ),
                        color: '#4257f6',
                    },
                    {
                        name: 'Sous traitance',
                        type: 'column',
                        data: this.billingXaxisCategories.map(
                            (d: Date) =>
                                -1 *
                                    monthlySub.find(
                                        (b) =>
                                            !b.isSent &&
                                            b.plannedDate.getTime() === d.getTime() &&
                                            d.getTime() > this.now.getTime(),
                                    )?.amountExclTax || 0,
                        ),
                        color: '#000000ae',
                    },
                ];

                this.billingXaxisCategories = this.billingXaxisCategories.map(
                    (x: Date) => `${x.getFullYear()}-${x.getMonth() + 1}-${x.getDate()}`,
                );

                //Tableaux:

                const data = billingMilestones?.map((billing: BillingMilestoneModel) => ({
                    ...billing,
                    plannedDate: new Date(billing.plannedDate),
                    code: billing.project?.code,
                    label: billing.project?.label,
                    isSentLabel: billing.isSent ? 'Oui' : 'Non',
                    leadLabel: billing.project?.leadUser?.lastname,
                    activityLabel: billing.project?.activity?.label,
                }));

                this.allBillingTotal = data.reduce((acc, b) => acc + b.amountExclTax, 0);
                this.allBillingPending = data
                    .filter((d) => !d.isSent)
                    .reduce((acc, b) => acc + b.amountExclTax, 0);
                const thisYearData = data.filter(
                    (d) => new Date(d.plannedDate).getFullYear() === this.now.getFullYear(),
                );
                this.thisYearBillingTotal = thisYearData.reduce(
                    (acc, b) => acc + b.amountExclTax,
                    0,
                );
                this.thisYearBillingPending = thisYearData
                    .filter((d) => !d.isSent)
                    .reduce((acc, b) => acc + b.amountExclTax, 0);

                const thisMonthData = data.filter(
                    (d) =>
                        new Date(d.plannedDate).getFullYear() === this.now.getFullYear() &&
                        new Date(d.plannedDate).getMonth() === this.now.getMonth(),
                );
                this.thisMonthBillingTotal = thisMonthData.reduce(
                    (acc, b) => acc + b.amountExclTax,
                    0,
                );
                this.thisMonthBillingPending = thisMonthData
                    .filter((d) => !d.isSent)
                    .reduce((acc, b) => acc + b.amountExclTax, 0);

                this.lateBilling = data
                    .filter(
                        (d) => new Date(d.plannedDate).getTime() < this.now.getTime() && !d.isSent,
                    )
                    .reduce((acc, b) => acc + b.amountExclTax, 0);

                this.billingSentData = new MatTableDataSource<{}>(data.filter((d) => d.isSent));

                this.billingPendingData = new MatTableDataSource<{}>(data.filter((d) => !d.isSent));

                this.headerService.setBreadcrumbs([
                    { label: 'Tableau de bord' },
                    { label: `Facturation` },
                ]);
            });
    }

    public goToProjectPage(id: any) {
        const billing = this.billingMilestones.find((f) => f.id === id);
        if (!billing || !billing.project?.id) return;
        this.router.navigate([`/main/projects/${billing.project?.code}/billing`]);
    }

    public onChangeYear(year: any) {
        console.log(year);
        console.log('here');
        if (!year) return;
        this.store.dispatch(new GetBillingMilestones({ year: Number(year) }));
    }
}
